import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, ComposedChart, LabelList
} from 'recharts';
import _ from 'lodash';

function App() {
  const [data, setData] = useState([]);
  const [creatorData, setCreatorData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fileUploaded, setFileUploaded] = useState(false);
  const [rawData, setRawData] = useState(null);
  const [insights, setInsights] = useState({
    totalBOMs: 0,
    totalAutomated: 0,
    overallRate: 0,
    peakPeriod: null,
    topCreators: []
  });
  const [viewMode, setViewMode] = useState('monthly');
  const [dateRange, setDateRange] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    
    if (file) {
      setLoading(true);
      
      const reader = new FileReader();
      
      reader.onload = (event) => {
        try {
          const binaryData = event.target.result;
          const workbook = XLSX.read(binaryData, { type: 'binary', cellDates: true });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);
          
          console.log("File processed successfully. Rows:", jsonData.length);
          
          // Store the raw data for reprocessing
          setRawData(jsonData);
          
          // Process initially
          const { timeData, creatorData, insights } = analyzeData(jsonData, viewMode, dateRange, customStartDate, customEndDate);
          setData(timeData);
          setCreatorData(creatorData);
          setInsights(insights);
          
          setFileUploaded(true);
          setLoading(false);
        } catch (error) {
          console.error("Error processing file:", error);
          alert("Error processing Excel file: " + error.message);
          setLoading(false);
        }
      };
      
      reader.onerror = () => {
        console.error("File reading failed");
        setLoading(false);
      };
      
      reader.readAsBinaryString(file);
    }
  };

  // This effect will run when viewMode or dateRange changes
  useEffect(() => {
    console.log("View mode changed to:", viewMode);
    if (rawData && rawData.length > 0) {
      const { timeData, creatorData, insights } = analyzeData(rawData, viewMode, dateRange, customStartDate, customEndDate);
      setData(timeData);
      setCreatorData(creatorData);
      setInsights(insights);
    }
  }, [viewMode, dateRange, customStartDate, customEndDate]); 

  const analyzeData = (rawData, mode, rangeSetting, startDate, endDate) => {
    console.log("Analyzing data with mode:", mode);
    
    // Filter data based on date range
    const now = new Date();
    let filterStartDate = null;
    let filterEndDate = null;

    switch (rangeSetting) {
      case 'last3m':
        filterStartDate = new Date();
        filterStartDate.setMonth(filterStartDate.getMonth() - 3);
        filterEndDate = new Date();
        break;
      case 'last6m':
        filterStartDate = new Date();
        filterStartDate.setMonth(filterStartDate.getMonth() - 6);
        filterEndDate = new Date();
        break;
      case 'ytd':
        filterStartDate = new Date(now.getFullYear(), 0, 1);
        filterEndDate = new Date();
        break;
      case 'custom':
        if (startDate && endDate) {
          filterStartDate = new Date(startDate);
          filterEndDate = new Date(endDate);
          filterEndDate.setHours(23, 59, 59, 999);
        }
        break;
      default:
        // 'all' - no date filtering
    }

    const filteredData = filterStartDate && filterEndDate
      ? rawData.filter(item => {
          if (!item['Created on']) return false;
          const itemDate = new Date(item['Created on']);
          return itemDate >= filterStartDate && itemDate <= filterEndDate;
        })
      : rawData;

    // Simplify creator data - treat BOMG and BOMG V2 as just "Automated"
    const processedData = filteredData.map(item => {
      const creator = item['Created by'] || 'Unknown';
      return {
        ...item,
        'CreatorType': ['BOMG', 'BOMG V2'].includes(creator) ? 'Automated' : creator
      };
    });

    // Creator analysis
    const creatorCounts = {};
    processedData.forEach(row => {
      const creator = row['CreatorType'];
      creatorCounts[creator] = (creatorCounts[creator] || 0) + 1;
    });

    const creatorList = Object.entries(creatorCounts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    // Top 5 creators + "Others"
    const topCreators = creatorList.slice(0, 5);
    const otherCreatorsCount = creatorList.slice(5).reduce((sum, item) => sum + item.value, 0);
    
    if (otherCreatorsCount > 0) {
      topCreators.push({ name: 'Others', value: otherCreatorsCount });
    }

    // Time-based analysis
    const dateGroups = _.groupBy(processedData, item => {
      if (!item['Created on']) return 'Unknown';
      
      const date = new Date(item['Created on']);
      if (mode === 'monthly') {
        return date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
      } else {
        const startOfYear = new Date(date.getFullYear(), 0, 1);
        const days = Math.floor((date - startOfYear) / (24 * 60 * 60 * 1000));
        const weekNum = Math.ceil((days + startOfYear.getDay() + 1) / 7);
        return `W${String(weekNum).padStart(2, '0')} ${date.getFullYear()}`;
      }
    });

    const timeData = Object.entries(dateGroups)
      .map(([period, items]) => {
        const date = items[0]['Created on'] ? new Date(items[0]['Created on']) : new Date();
        
        const automated = items.filter(item => item['CreatorType'] === 'Automated').length;
        const total = items.length;
        const manual = total - automated;
        
        return {
          period,
          date,
          automated,
          manual,
          total,
          automationRate: total > 0 ? Math.round((automated / total) * 100) : 0
        };
      })
      .sort((a, b) => a.date - b.date);

    // Overall metrics
    const totalBOMs = filteredData.length;
    const totalAutomated = processedData.filter(item => item['CreatorType'] === 'Automated').length;
    const overallRate = totalBOMs > 0 ? Math.round((totalAutomated / totalBOMs) * 100) : 0;
    
    // Find peak month/week
    const peakPeriod = _.maxBy(timeData, 'total');
    
    // Calculate trend
    const lastPeriod = timeData[timeData.length - 1];
    const prevPeriod = timeData[timeData.length - 2];
    const periodChange = lastPeriod && prevPeriod && prevPeriod.automationRate > 0
      ? Math.round(((lastPeriod.automationRate - prevPeriod.automationRate) / prevPeriod.automationRate) * 100)
      : 0;

    return {
      timeData,
      creatorData: topCreators,
      insights: {
        totalBOMs,
        totalAutomated,
        totalManual: totalBOMs - totalAutomated,
        overallRate,
        periodChange,
        peakPeriod,
        topCreators: creatorList.slice(0, 3)
      }
    };
  };

  const customTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: 'white',
          padding: '10px',
          border: '1px solid #ccc',
          borderRadius: '5px'
        }}>
          <p><strong>{label}</strong></p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {entry.value} ({Math.round((entry.value / insights.totalBOMs) * 100)}%)
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column'
      }}>
        <h2>Processing data...</h2>
        <p>This may take a few moments for large files</p>
      </div>
    );
  }

  if (!fileUploaded) {
    return (
      <div style={{ 
        margin: '50px auto', 
        padding: '30px', 
        border: '2px dashed #ccc',
        borderRadius: '10px',
        textAlign: 'center',
        maxWidth: '500px'
      }}>
        <h2>BOM Analysis Dashboard</h2>
        <p>Upload your Excel file containing BOM data to generate the dashboard</p>
        <input 
          type="file" 
          onChange={handleFileUpload} 
          accept=".xlsx, .xls"
          style={{ 
            margin: '20px 0', 
            padding: '10px',
            border: '1px solid #ddd',
            borderRadius: '5px',
            width: '100%'
          }}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '20px' }}>BOM Creation Analysis Dashboard</h1>
      
      {/* Controls */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '20px',
        padding: '15px',
        backgroundColor: '#f5f5f5',
        borderRadius: '8px'
      }}>
        <div>
          <button 
            onClick={() => setFileUploaded(false)} 
            style={{
              padding: '8px 16px',
              backgroundColor: '#4a90e2',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              marginRight: '15px'
            }}
          >
            Upload New File
          </button>
          
          <select 
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            style={{
              padding: '8px',
              borderRadius: '4px',
              border: '1px solid #ddd',
              marginRight: '10px'
            }}
          >
            <option value="all">All Time</option>
            <option value="last3m">Last 3 Months</option>
            <option value="last6m">Last 6 Months</option>
            <option value="ytd">Year to Date</option>
            <option value="custom">Custom Range</option>
          </select>
          
          {dateRange === 'custom' && (
            <span>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                style={{
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  marginRight: '5px'
                }}
              />
              <span>to</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                style={{
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  marginLeft: '5px'
                }}
              />
            </span>
          )}
        </div>
        
        <div>
          <button
            onClick={() => setViewMode('weekly')}
            style={{
              padding: '8px 16px',
              backgroundColor: viewMode === 'weekly' ? '#4a90e2' : '#f1f1f1',
              color: viewMode === 'weekly' ? 'white' : 'black',
              border: 'none',
              borderRadius: '4px 0 0 4px',
              cursor: 'pointer'
            }}
          >
            Weekly
          </button>
          <button
            onClick={() => setViewMode('monthly')}
            style={{
              padding: '8px 16px',
              backgroundColor: viewMode === 'monthly' ? '#4a90e2' : '#f1f1f1',
              color: viewMode === 'monthly' ? 'white' : 'black',
              border: 'none',
              borderRadius: '0 4px 4px 0',
              cursor: 'pointer'
            }}
          >
            Monthly
          </button>
        </div>
      </div>
      
      {/* Key Metrics */}
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
        gap: '15px',
        marginBottom: '30px'
      }}>
        <div style={{ 
          padding: '20px', 
          borderRadius: '8px', 
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          backgroundColor: 'white' 
        }}>
          <h3 style={{ marginTop: 0, color: '#666' }}>Total BOMs</h3>
          <p style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>
            {insights.totalBOMs?.toLocaleString()}
          </p>
        </div>
        
        <div style={{ 
          padding: '20px', 
          borderRadius: '8px', 
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          backgroundColor: 'white' 
        }}>
          <h3 style={{ marginTop: 0, color: '#666' }}>Automated BOMs</h3>
          <p style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>
            {insights.totalAutomated?.toLocaleString()} 
            <span style={{ fontSize: '16px', color: '#666', fontWeight: 'normal' }}> ({insights.overallRate}%)</span>
          </p>
        </div>
        
        <div style={{ 
          padding: '20px', 
          borderRadius: '8px', 
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          backgroundColor: 'white' 
        }}>
          <h3 style={{ marginTop: 0, color: '#666' }}>Manual BOMs</h3>
          <p style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>
            {insights.totalManual?.toLocaleString()}
            <span style={{ fontSize: '16px', color: '#666', fontWeight: 'normal' }}> ({100 - insights.overallRate}%)</span>
          </p>
        </div>
        
        {insights.periodChange !== 0 && (
          <div style={{ 
            padding: '20px', 
            borderRadius: '8px', 
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            backgroundColor: 'white' 
          }}>
            <h3 style={{ marginTop: 0, color: '#666' }}>Trend</h3>
            <p style={{ 
              fontSize: '24px', 
              fontWeight: 'bold', 
              margin: 0,
              color: insights.periodChange > 0 ? '#4CAF50' : '#F44336'
            }}>
              {insights.periodChange > 0 ? '+' : ''}{insights.periodChange}%
              <span style={{ fontSize: '16px', color: '#666', fontWeight: 'normal' }}> since previous {viewMode}</span>
            </p>
          </div>
        )}
      </div>
      
      {/* Charts */}
      <div style={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(500px, 1fr))',
        gap: '20px',
        marginBottom: '30px'
      }}>
        {/* Volume Chart */}
        <div style={{ 
          padding: '20px', 
          borderRadius: '8px', 
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          backgroundColor: 'white' 
        }}>
          <h2 style={{ marginTop: 0 }}>BOM Creation Volume</h2>
          <div style={{ height: '400px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="period" 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  interval={0}
                />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="automated" stackId="a" fill="#4CAF50" name="Automated">
                  <LabelList dataKey="automated" position="center" fill="white" />
                </Bar>
                <Bar dataKey="manual" stackId="a" fill="#FF9800" name="Manual">
                  <LabelList dataKey="manual" position="center" fill="white" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        
        {/* Creator Distribution Chart */}
        <div style={{ 
          padding: '20px', 
          borderRadius: '8px', 
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          backgroundColor: 'white' 
        }}>
          <h2 style={{ marginTop: 0 }}>BOM Creation by User</h2>
          <div style={{ height: '400px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={creatorData}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  outerRadius={120}
                  fill="#8884d8"
                  dataKey="value"
                  nameKey="name"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                >
                  {creatorData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={customTooltip} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      
      {/* Automation Rate Trend */}
      <div style={{ 
        padding: '20px', 
        borderRadius: '8px', 
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        backgroundColor: 'white',
        marginBottom: '30px'
      }}>
        <h2 style={{ marginTop: 0 }}>Automation Rate Trend</h2>
        <div style={{ height: '400px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="period" 
                angle={-45}
                textAnchor="end"
                height={80}
                interval={0}
              />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" domain={[0, 100]} />
              <Tooltip />
              <Legend />
              <Bar dataKey="total" yAxisId="left" fill="#2196F3" name="Total Volume">
                <LabelList dataKey="total" position="top" />
              </Bar>
              <Line
                type="monotone"
                dataKey="automationRate"
                yAxisId="right"
                stroke="#FF5722"
                name="Automation Rate (%)"
                strokeWidth={2}
              >
                <LabelList dataKey="automationRate" position="top" formatter={(value) => `${value}%`} />
              </Line>
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Insights Section */}
      <div style={{ 
        padding: '20px', 
        borderRadius: '8px', 
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        backgroundColor: 'white' 
      }}>
        <h2 style={{ marginTop: 0 }}>Key Insights</h2>
        
        <div style={{ marginBottom: '15px' }}>
          <h3 style={{ color: '#333' }}>Peak Volume</h3>
          <p>
            The highest BOM creation volume was in {insights.peakPeriod?.period} with {insights.peakPeriod?.total} BOMs
            ({insights.peakPeriod?.automationRate}% automation rate).
          </p>
        </div>
        
        <div style={{ marginBottom: '15px' }}>
          <h3 style={{ color: '#333' }}>Top Contributors</h3>
          <p>
            The top BOM creators are:
            </p>
            <ul>
              {insights.topCreators?.map((creator, index) => (
                <li key={index}>{creator.name}: {creator.value} BOMs ({Math.round((creator.value / insights.totalBOMs) * 100)}%)</li>
              ))}
            </ul>
        </div>
        
        {data.length > 1 && (
          <div>
            <h3 style={{ color: '#333' }}>Trend Analysis</h3>
            <p>
              BOM creation has {data[data.length - 1].total > data[0].total ? 'increased' : 'decreased'} from {' '}
              {data[0].total} to {data[data.length - 1].total} BOMs per {viewMode === 'monthly' ? 'month' : 'week'}.
              The automation rate has {data[data.length - 1].automationRate > data[0].automationRate ? 'improved' : 'declined'} from {' '}
              {data[0].automationRate}% to {data[data.length - 1].automationRate}%.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;